import { useState, useEffect } from "react";
import { ArrowLeft, ChartNoAxesCombined } from "lucide-react";
import { Link } from "react-router-dom";
import PageTitleScript from "@/components/MetaScript";
import { useTrackedExercises } from "@/hooks/useTrackedExercises";
import { CustomLoader } from "@/components/ui/CustomLoader";
import { CustomLoadingError } from "@/components/ui/CustomLoadingError";
import CustomNoData from "@/components/ui/CustomNoData";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { exerciseApi } from "@/api/exerciseApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useMutation } from "@tanstack/react-query";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";
import { AxiosError } from "axios";
import { useValidationErrors } from "@/hooks/useValidationErrors";

export function SettingsExerciseDetailPage() {
  // Use the useParams hook to get the route parameters
  const { exerciseNanoId } = useParams<{ exerciseNanoId: string }>();
  const [exerciseName, setExerciseName] = useState<string>("");
  const { validationErrors, removeValidationError, clearAllValidationErrors, setValidationErrorsFromResponse } = useValidationErrors();
  const { handleSuccess, handleError } = useToastHandler();
  const invalidate = useInvalidateResources();

  const { data: exercises, isLoading, isError } = useTrackedExercises();

  // find exercise by exerciseNanoId
  const exercise = exercises?.find(
    (exercise) => exercise.nano_id === exerciseNanoId
  );

  // Set exercise name when exercise data is loaded
  useEffect(() => {
    if (exercise) {
      setExerciseName(exercise.name);
    }
  }, [exercise]);


  const handleNameChange = (value: string) => {
    setExerciseName(value);
    // Clear validation errors when user starts typing
    removeValidationError('name');
  };

  // Use mutation for updating exercise name
  const mutation = useMutation({
    mutationFn: async () => {
      return await exerciseApi.updateExerciseAttributes(
        exerciseNanoId as string,
        exerciseName.trim()
      );
    },
    onSuccess: () => {
      handleSuccess("Exercise name updated successfully", 1000);
      // Clear validation errors on success
      clearAllValidationErrors();
      // Invalidate the exercises cache to refresh data
      invalidate(["/api/exercises/tracked"]);
    },
    onError: (error) => {
      if (
        error instanceof AxiosError &&
        error.response?.data?.validation_errors
      ) {
        // validation_errors example:
        //   {
        //     "name": [
        //         "The name field must be at least 2 characters."
        //     ]
        // }
        // Store validation errors to display under inputs
        setValidationErrorsFromResponse(error.response.data.validation_errors);
        //return; // Don't show generic error toast for validation errors
      }
      handleError(error, 3000);
    },
  });

  if (isLoading) {
    return <CustomLoader />;
  }

  if (isError) {
    return <CustomLoadingError />;
  }

  return (
    <div className="flex flex-col p-6">
      <PageTitleScript title={exercise?.name ?? "Exercise"} />
      <div className="flex items-center mb-6">
        <Link to="/settings/exercises" className="mr-2">
          <ArrowLeft size={24} />
        </Link>
        <h1 className="text-2xl font-bold text-tx-dark">{exercise?.name}</h1>
      </div>

      {!exercise ? (
        <CustomNoData msg="No exercises found" />
      ) : (
        <div>
          <Card className="rounded-xl overflow-hidden shadow-md py-10 bg-white border border-bd-light">
            <div className="px-6">
              {/* Exercise Name Input */}
              <div className="mb-6">
                <label
                  htmlFor="exercise-name"
                  className="block text-sm font-medium mb-2"
                >
                  Exercise Name
                </label>
                <Input
                  id="exercise-name"
                  value={exerciseName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full ${
                    validationErrors.name ? "border-red-500" : ""
                  }`}
                  placeholder="Enter exercise name"
                />
                {validationErrors.name && (
                  <div className="mt-1">
                    {validationErrors.name.map((error, index) => (
                      <p key={index} className="text-sm text-tx-error">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {
                  if (!exerciseName.trim() || exerciseName === exercise.name)
                    return;
                  mutation.mutate();
                }}
              >
                {mutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </Card>

          <div className="mt-6">
            <Button asChild variant="link" size="lg" className="w-full">
              <Link
                to={`/stats/exercise-overview/${exerciseNanoId}`}
                className="flex items-center justify-center gap-2"
              >
                <ChartNoAxesCombined size={20} />
                <span>View Exercise Stats</span>
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
